import React, { useEffect, useState, lazy, Suspense } from 'react';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme as useTierTheme } from './contexts/ThemeContext';
// Eager imports — entry points and always-mounted chrome. Kept out of the lazy
// split so the first paint (landing/login) and the persistent shell load immediately.
import Navigation from './components/Navigation';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import GlobalFooter from './components/GlobalFooter';
import { AuthProvider, useAuth, Permissions } from './contexts/AuthContext';
import { SecurityProvider } from './contexts/SecurityContext';
import { multiFactor } from 'firebase/auth';
import { roleRequiresMfa, MFA_ENROLL_PATH } from './config/security';
import { NavigationProvider } from './contexts/NavigationContext';
import { setupCSP } from './utils/security';
import { initializeNetworkMonitoring } from './utils/networkCheck';
import { initializeFirebase } from './firebase';

// Route-level components are code-split: each loads on first navigation to its
// route (Suspense fallback below), so the initial bundle no longer carries every
// screen up front. Biggest wins are the heavy authenticated screens (Employees,
// SubscriptionManagement, CompanyManagement, Reporting, Payroll).
const Dashboard = lazy(() => import('./components/Dashboard'));
const Employees = lazy(() => import('./components/Employees'));
const Payroll = lazy(() => import('./components/Payroll'));
const Settings = lazy(() => import('./components/Settings'));
const Register = lazy(() => import('./components/Register'));
const Profile = lazy(() => import('./components/Profile'));
const Support = lazy(() => import('./components/Support'));
const CompanyManagement = lazy(() => import('./components/CompanyManagement'));
const CompanyDetails = lazy(() => import('./components/CompanyDetails'));
const MfaEnrollGate = lazy(() => import('./components/security/MfaEnrollGate'));
const ForgotPassword = lazy(() => import('./components/security/ForgotPassword'));
const FirebaseTroubleshooting = lazy(() => import('./components/FirebaseTroubleshooting'));
const AutomatedPayroll = lazy(() => import('./components/AutomatedPayroll'));
const PayrollInsights = lazy(() => import('./components/PayrollInsights'));
const BankAccountSetup = lazy(() => import('./components/BankAccountSetup'));
const TaxDocuments = lazy(() => import('./components/TaxDocuments'));
const Messages = lazy(() => import('./components/messages/Messages'));
const Friends = lazy(() => import('./components/Friends'));
const AIHelpBot = lazy(() => import('./components/AIHelpBot'));
const SubscriptionManagement = lazy(() => import('./components/subscription/SubscriptionManagement'));
const Reporting = lazy(() => import('./components/Reporting'));
const OwnerDashboardIndex = lazy(() => import('./components/owner/OwnerDashboardIndex'));
const EmployeeDashboardIndex = lazy(() => import('./components/employee/EmployeeDashboardIndex'));
// Named export — adapt to the default-export shape React.lazy expects.
const EmployeeTaxManagement = lazy(() =>
  import('./components/EmployeeTaxManagement').then((m) => ({ default: m.EmployeeTaxManagement }))
);
const TimeClockPage = lazy(() => import('./components/TimeClockPage'));
const TimeRecordsPage = lazy(() => import('./components/TimeRecordsPage'));

// Static marketing pages — also code-split.
const AboutPage = lazy(() => import('./components/static/AboutPage'));
const PricingPage = lazy(() => import('./components/static/PricingPage'));
const FeaturesPage = lazy(() => import('./components/static/FeaturesPage'));
const ContactPage = lazy(() => import('./components/static/ContactPage'));
const PrivacyPolicy = lazy(() => import('./components/static/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/static/TermsOfService'));

// Debug components are loaded only outside production builds. Bundlers tree-shake
// the require() branch when NODE_ENV === 'production' (CRA sets it for builds).
const IS_DEBUG_BUILD = process.env.NODE_ENV !== 'production';
const debugModules = IS_DEBUG_BUILD
  ? {
      AuthDebugger: require('./components/debugging/AuthDebugger').default,
      ProfileTroubleshooting: require('./components/debugging/ProfileTroubleshooting').default,
      ForceProfileCreation: require('./components/debugging/ForceProfileCreation').default,
      FirebaseConnectionTest: require('./components/debugging/FirebaseConnectionTest').default,
      ProfileCreationTool: require('./components/debugging/ProfileCreationTool').default,
      DebugTools: require('./components/debugging/DebugTools').default,
      StorageTester: require('./components/debugging/StorageTester').default,
      ResetFriendRequests: require('./components/debugging/ResetFriendRequests').default,
      EmailTester: require('./components/debugging/EmailTester').default,
    }
  : null;

// Loading component with optional error message and initialization steps
const LoadingScreen = ({ error, retryFn }: { error?: string; retryFn?: () => void }) => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'white',
      zIndex: 9999,
    }}
  >
    {error ? (
      <>
        <Typography variant="h6" color="error" gutterBottom>
          Application Error
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 600, mb: 2 }}>
          {error}
        </Typography>
        {retryFn && (
          <button
            onClick={retryFn}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        )}
      </>
    ) : (
      <>
        <CircularProgress size={50} />
        <Box sx={{ mt: 2, color: 'text.secondary' }}>Loading application...</Box>
      </>
    )}
  </Box>
);

// Private Route component to handle authentication + role-based access.
interface PrivateRouteProps {
  element: React.ReactElement;
  // When set, the signed-in user must hold this permission to view the route.
  // Unauthorized users (e.g. Employees hitting admin pages) go to their own dashboard.
  requiredPermission?: keyof Permissions;
  // When set, only the platform Owner (god-mode) may view the route.
  ownerOnly?: boolean;
  // When set, the platform Owner is redirected to their console. Keeps the owner out
  // of employee/social surfaces (Friends, Messages, the personal employee dashboard).
  blockOwner?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element, requiredPermission, ownerOnly, blockOwner }) => {
  const { currentUser, userProfile, loading, hasPermission, isOwner } = useAuth();

  if (loading) return <LoadingScreen />;

  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: window.location.pathname }} />;
  }

  // Approval gate: Firebase auto-signs-in a brand-new account at signup, so a
  // still-pending (or rejected) user could otherwise reach protected areas
  // before approval. Bounce any non-owner whose account isn't approved back to
  // login. The Owner is always exempt.
  const acctStatus = (userProfile as any)?.status;
  const acctApproved = (userProfile as any)?.accountApproved;
  if (!isOwner && userProfile && (acctStatus === 'pending' || acctStatus === 'rejected' || acctApproved === false)) {
    return <Navigate to="/login" replace />;
  }

  if (ownerOnly && !isOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  if (blockOwner && isOwner) {
    return <Navigate to="/owner-dashboard" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/employee-dashboard" replace />;
  }

  // MFA enforcement (inert unless ENFORCE_MFA is enabled — see config/security.ts).
  // High-privilege roles without a second factor are routed to the enrollment gate;
  // the gate path is exempt to avoid a redirect loop.
  if (
    roleRequiresMfa(userProfile?.role) &&
    multiFactor(currentUser).enrolledFactors.length === 0 &&
    window.location.pathname !== MFA_ENROLL_PATH
  ) {
    return <Navigate to={MFA_ENROLL_PATH} replace />;
  }

  return <Navigation>{element}</Navigation>;
};

// Component to handle the landing page for authenticated users
// If user is authenticated, they should stay on the landing page (not be redirected)
const AuthAwareLandingPage: React.FC = () => {
  const { currentUser, isOwner } = useAuth();
  const location = useLocation();

  // Only redirect if the user didn't explicitly navigate to the home page
  const shouldRedirect = currentUser && location.state?.from !== '/';
  if (!shouldRedirect) return <LandingPage />;
  // The App Owner lands on their oversight console; everyone else on the dashboard.
  return <Navigate to={isOwner ? '/owner-dashboard' : '/dashboard'} replace />;
};

// True for webpack's "chunk failed to load" errors. These happen when a tab is
// running a build that a newer deploy has since replaced: the old code asks for a
// chunk hash that no longer exists, the SPA rewrite serves index.html in its place,
// and the browser throws trying to execute HTML as JS.
function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}

// Error boundary component
class ErrorBoundary extends React.Component<any, { hasError: boolean, error: any, reloading: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  static getDerivedStateFromError(error: any) {
    // Optimistically assume a chunk error will be cured by a reload, so render shows
    // a benign "updating" message instead of the red error flash.
    return { hasError: true, error, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: any, errorInfo: any) {
    if (isChunkLoadError(error)) {
      // index.html is served no-cache, so a reload pulls the fresh build and the new
      // chunk hashes. Guard with a short-lived sessionStorage stamp so we reload at
      // most once per window — if a chunk is genuinely missing from the live deploy,
      // we fall through to the real error UI instead of looping forever.
      const KEY = 'chunkReloadAt';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return;
      }
      // Reload already tried and didn't help — show the error rather than spin.
      this.setState({ reloading: false });
    }
    console.error('App Error:', error);
    console.error('Error Info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.reloading) {
        return (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <h1>Updating to the latest version…</h1>
          </div>
        );
      }
      return (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <h1>Something went wrong.</h1>
          <pre style={{ color: 'red' }}>{this.state.error?.toString()}</pre>
          <button onClick={() => window.location.reload()}>Reload App</button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Drives the theme variant from the signed-in user's tier so each dashboard is
// visually distinct: App Owner → dark red, Company/Admin → dark blue,
// Employee → light. Lives inside both ThemeProvider and AuthProvider.
function ThemeTierSync() {
  const { isOwner, userProfile, currentUser } = useAuth();
  const { setVariant } = useTierTheme();
  React.useEffect(() => {
    if (!currentUser) { setVariant('company'); return; } // reset to blue when signed out
    if (isOwner) setVariant('owner');
    else if (userProfile?.role === 'Employee') setVariant('employee');
    else setVariant('company');
  }, [isOwner, userProfile?.role, currentUser, setVariant]);
  return null;
}

// Exposes window.__repairEscapedData() for the platform owner only. Runs a
// one-time, client-side repair that decodes legacy HTML-entity-encoded text
// fields in Firestore. The owner has god-mode writes via firestore.rules, so no
// Cloud Function is needed. Idempotent — safe to run more than once. The same
// routine is also available as a button in the Owner Console.
function OwnerRepairHook() {
  const { isOwner } = useAuth();
  React.useEffect(() => {
    if (!isOwner) return;
    (window as any).__repairEscapedData = async () => {
      const { repairEscapedData } = await import('./utils/dataRepair');
      const result = await repairEscapedData();
      // eslint-disable-next-line no-console
      console.log('repairEscapedData result:', result);
      return result;
    };
    return () => { delete (window as any).__repairEscapedData; };
  }, [isOwner]);
  return null;
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | undefined>();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Wire CSP at app startup. setupCSP is a no-op when REACT_APP_CSP_ENABLED is unset.
        try { setupCSP(); } catch (cspErr) { console.warn('CSP setup failed:', cspErr); }

        // Optional network monitor.
        try { initializeNetworkMonitoring(); } catch (netErr) { console.warn('Network monitor init failed:', netErr); }

        const firebaseInit = await initializeFirebase();
        if (!firebaseInit.success) {
          throw new Error(firebaseInit.error || 'Failed to initialize Firebase');
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error during app initialization:', error);
        setInitError(error instanceof Error ? error.message : 'Failed to initialize application');
      }
    };

    initializeApp();
  }, []);

  if (!isInitialized) {
    return <LoadingScreen error={initError} retryFn={initError ? () => window.location.reload() : undefined} />;
  }

  return (
    <ErrorBoundary>
      <SecurityProvider>
        <Router>
          <ThemeProvider>
            <AuthProvider>
              <NavigationProvider>
                <CssBaseline />
                <ThemeTierSync />
                <OwnerRepairHook />
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <Suspense fallback={<LoadingScreen />}>
                  <Routes>
                    <Route path="/" element={<AuthAwareLandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    
                    {/* Protected Routes */}
                    <Route path="/dashboard" element={<PrivateRoute requiredPermission="viewReports" element={<Dashboard />} />} />
                    <Route path="/employees" element={<PrivateRoute requiredPermission="viewEmployees" element={<Employees />} />} />
                    <Route path="/payroll" element={<PrivateRoute requiredPermission="viewPayroll" element={<Payroll />} />} />
                    <Route path="/settings" element={<PrivateRoute requiredPermission="viewSettings" element={<Settings />} />} />
                    <Route path={MFA_ENROLL_PATH} element={<PrivateRoute element={<MfaEnrollGate />} />} />
                    <Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
                    <Route path="/support" element={<PrivateRoute element={<Support />} />} />
                    <Route path="/companies" element={<PrivateRoute requiredPermission="viewCompanies" element={<CompanyManagement />} />} />
                    {/* Company detail view — CompanyManagement navigates to /companies/:id */}
                    <Route path="/companies/:id" element={<PrivateRoute requiredPermission="viewCompanies" element={<CompanyDetails />} />} />
                    <Route path="/company-details/:id" element={<PrivateRoute requiredPermission="viewCompanies" element={<CompanyDetails />} />} />
                    <Route path="/company-details" element={<PrivateRoute requiredPermission="viewCompanies" element={<CompanyDetails />} />} />
                    <Route path="/automated-payroll" element={<PrivateRoute requiredPermission="processPayroll" element={<AutomatedPayroll />} />} />
                    <Route path="/payroll-insights" element={<PrivateRoute requiredPermission="viewPayroll" element={<PayrollInsights />} />} />
                    <Route path="/bank-account-setup" element={<PrivateRoute requiredPermission="viewPayroll" element={<BankAccountSetup />} />} />
                    <Route path="/tax-documents" element={<PrivateRoute requiredPermission="viewReports" element={<TaxDocuments />} />} />
                    <Route path="/messages/*" element={<PrivateRoute blockOwner element={<Messages />} />} />
                    <Route path="/friends" element={<PrivateRoute blockOwner element={<Friends />} />} />
                    <Route path="/subscription" element={<PrivateRoute requiredPermission="manageSettings" element={<SubscriptionManagement />} />} />
                    <Route path="/reporting" element={<PrivateRoute requiredPermission="viewReports" element={<Reporting />} />} />
                    <Route path="/employee-dashboard" element={<PrivateRoute blockOwner element={<EmployeeDashboardIndex />} />} />
                    <Route path="/time-clock" element={<PrivateRoute blockOwner element={<TimeClockPage />} />} />
                    <Route path="/time-records" element={<PrivateRoute requiredPermission="viewEmployees" element={<TimeRecordsPage />} />} />
                    <Route path="/owner-dashboard" element={<PrivateRoute ownerOnly element={<OwnerDashboardIndex />} />} />
                    <Route path="/employee-tax" element={<PrivateRoute element={<EmployeeTaxManagement />} />} />
                    
                    {/* Debug Routes — non-production builds only. */}
                    {IS_DEBUG_BUILD && debugModules && (
                      <>
                        <Route path="/debug/auth" element={<debugModules.AuthDebugger />} />
                        <Route path="/debug/profile" element={<debugModules.ProfileTroubleshooting />} />
                        <Route path="/debug/force-profile" element={<debugModules.ForceProfileCreation />} />
                        <Route path="/debug/firebase" element={<debugModules.FirebaseConnectionTest />} />
                        <Route path="/debug/profile-tool" element={<debugModules.ProfileCreationTool />} />
                        <Route path="/debug/tools" element={<debugModules.DebugTools />} />
                        <Route path="/debug/storage" element={<debugModules.StorageTester />} />
                        <Route path="/debug/reset-friends" element={<debugModules.ResetFriendRequests />} />
                        <Route path="/debug/email" element={<debugModules.EmailTester />} />
                        <Route path="/debug/firebase-troubleshoot" element={<FirebaseTroubleshooting />} />
                      </>
                    )}
                  </Routes>
                  </Suspense>
                  <GlobalFooter />
                  <Suspense fallback={null}>
                    <AIHelpBot />
                  </Suspense>
                </Box>
              </NavigationProvider>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </SecurityProvider>
    </ErrorBoundary>
  );
}

export default App;
